import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import HorizontalRule from 'components/HorizontalRule';
import { rateForState } from 'components/AverageRatesModal/Constants';
import MarketplaceStatsModal from './MarketplaceStatsModal';
import './NewJobPosterDashboard.scss';

const HELP_ARTICLES = [
  { label: 'How does the hiring process work?' },
  { label: 'How do I post a job on Go4?' },
  { label: 'Do I need to send ATs a 1099?' },
];

// Maps user.defaultEventSettingId (org-type id from ORGANIZATION_TYPES in helpers/enums)
// to the CASE_STUDIES key. performing_arts (id 2) intentionally has no case study.
const SETTING_ID_TO_ORG_TYPE = {
  0: 'industrial',
  1: 'medical',
  2: 'performing_arts',
  3: 'school',
  4: 'sports',
  5: 'facility',
};

const CASE_STUDIES = {
  school: {
    title: "When the Sideline Can't Go Empty",
    url: 'https://www.go4.io/when-the-sideline-cant-go-empty/',
  },
  sports: {
    title: 'Every Whistle, Every Weekend',
    url: 'https://www.go4.io/every-whistle-every-weekend/',
  },
  facility: {
    title: 'When the Building Never Stops',
    url: 'https://www.go4.io/when-the-building-never-stops/',
  },
  medical: {
    title: 'The Sideline Is the Front Door to Care',
    url: 'https://www.go4.io/athletic-trainers-for-hospitals-and-clinics/',
  },
  industrial: {
    title: 'Athletic Trainers on the Factory Floor',
    url: 'https://www.go4.io/athletic-trainers-on-the-factory-floor/',
  },
};

const formatNumber = n => (n == null ? '—' : Number(n).toLocaleString());

class NewJobPosterDashboard extends Component {
  state = { modalOpen: false };

  openModal = () => this.setState({ modalOpen: true });
  closeModal = () => this.setState({ modalOpen: false });

  render() {
    const { user, marketplaceStats } = this.props;
    const { modalOpen } = this.state;
    const {
      atsOnPlatform,
      hoursPosted,
      jobsPosted,
      atsWithin50Miles,
      currentZip,
      loading,
    } = marketplaceStats;

    const displayZip = currentZip || user.addressZip || 'XXXXX';
    const displayState = marketplaceStats.currentState || user.state || 'your area';

    const initials = [user.firstName, user.lastName]
      .filter(Boolean)
      .map(n => n[0].toUpperCase())
      .join('');

    const orgType = SETTING_ID_TO_ORG_TYPE[user.defaultEventSettingId];
    const jpType = orgType ? orgType.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase()) : 'JP Type';
    const caseStudy = CASE_STUDIES[orgType];

    const platformStats = [
      { label: 'ATs On The Platform', value: formatNumber(atsOnPlatform) },
      { label: 'Hours Posted On The Platform', value: formatNumber(hoursPosted) },
      { label: 'Jobs Posted On The Platform', value: formatNumber(jobsPosted) },
    ];

    const rateDisplay = loading ? '—' : (rateForState(displayState) || '—');

    return (
      <div className="new-jp-dashboard">
        <div className="njp-header">
          <h1 className="njp-welcome">{user.firstName}, welcome to Go4</h1>
          <div className="njp-org">
            <span className="njp-org-name">{user.companyName || 'Organization Name'}</span>
            <div className="njp-avatar">
              {user.profilePic ? <img src={user.profilePic} alt="profile" /> : initials}
            </div>
          </div>
        </div>

        <HorizontalRule margin="0" />

        <div className="njp-trainers-card">
          <h5 className="njp-card-title">Athletic Trainers In Your Area</h5>
          <div className="njp-trainers-count-row">
            <div className="njp-count-col">
              <span className="njp-trainers-count">{loading ? '—' : formatNumber(atsWithin50Miles)}</span>
            </div>
            <span className="njp-trainers-desc">
              verified athletic trainers within 50 miles of zip code {displayZip}
            </span>
          </div>

          <HorizontalRule margin="0" dashed />

          <div className="njp-rate-row">
            <span className="njp-rate-text">
              Avg Transacted Rate In {displayState}: {rateDisplay}
            </span>
            <button className="njp-change-zip" onClick={this.openModal}>
              Change zip code
            </button>
          </div>
        </div>

        <div className="njp-trainers-card">
          <div className="njp-platform-stats">
            {platformStats.map(({ label, value }, i) => (
              <div key={i} className="njp-stat">
                <span className="njp-stat-label">{label}</span>
                <span className="njp-stat-value">{loading ? '—' : value}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="njp-draft-card">
          <p className="njp-draft-heading">Create a Draft</p>
          <HorizontalRule dashed margin="8px" />
          <div className="njp-draft-content">
            <p className="njp-draft-desc">
              Want to take the Go4 platform for a test drive?
              <br />
              Create a draft of your job before posting.
            </p>
            <Link className="njp-draft-btn" to="/v2/create-event/setting">
              CREATE A DRAFT
            </Link>
          </div>
        </div>

        <div className="njp-bottom-row">
          <div className="njp-help-articles">
            <div className="njp-section-header">TOP HELP ARTICLES</div>
            <div className="njp-articles-list">
              {HELP_ARTICLES.map(({ label }, i) => (
                <p key={i} className="njp-article-link">
                  {label}
                </p>
              ))}
            </div>
          </div>

          <div className="njp-case-study">
            <p className="njp-case-study-type">[{jpType}] Case Study</p>
            {caseStudy && (
              <>
                <p className="njp-case-study-title">{caseStudy.title}</p>
                <div className="njp-case-study-rule" />
                <a className="njp-read-more" href={caseStudy.url} target="_blank" rel="noopener noreferrer">
                  Read More...
                </a>
              </>
            )}
          </div>
        </div>

        <MarketplaceStatsModal isOpen={modalOpen} onClose={this.closeModal} />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  user: state.session.currentUser,
  marketplaceStats: state.dashboard.marketplaceStats,
});

export default connect(mapStateToProps)(NewJobPosterDashboard);
